import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  PLATFORM_ID,
  Renderer2,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

import * as env from 'environments/environment';

import { CommonService } from '@services/common.service';
import { SharedService } from '@services/shared.service';

interface SocialNetwork {
  socialNetworkingName?: string;
  webUrl?: string;
  [key: string]: unknown;
}

@Component({
  selector: 'app-footer-page',
  templateUrl: './footer-page.component.html',
  styleUrls: ['./footer-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterPageComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly renderer = inject(Renderer2);
  private readonly commonService = inject(CommonService);
  private readonly sharedService = inject(SharedService);
  private readonly router = inject(Router);
  private readonly platformId = inject(PLATFORM_ID);

  /*
   * Angular 18 signal-based viewChild
   */
  private readonly cookieDiv =
    viewChild<ElementRef<HTMLElement>>('cookie_div');

  /*
   * Environment
   */
  readonly appEnv = env.environment;

  /*
   * UI state
   */
  readonly currentYear = signal(new Date().getFullYear());

  readonly socialNetwork = signal<SocialNetwork[]>([]);

  readonly isCookieExists = signal(true);

  readonly enableLoginBtn = signal(true);

  constructor() {
    this.initializeUserState();
    this.initializeSocialNetwork();
  }

  /**
   * User login/logout state
   */
  private initializeUserState(): void {
    this.sharedService.userDetailsParam
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((userData) => {
        this.enableLoginBtn.set(!userData);
      });

    this.sharedService.logOutUserId
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((status) => {
        if (status !== null && status !== undefined) {
          this.enableLoginBtn.set(!status);
        }
      });
  }

  /**
   * Social network
   */
  private initializeSocialNetwork(): void {
    this.getSocialNetwork();

    this.commonService.SocialNetworkSuccess
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => {
        if (response?.result) {
          this.socialNetwork.set(response.result);
        }
      });
  }

  /**
   * Request social network information
   */
  private getSocialNetwork(): void {
    this.commonService.getSocialNetworking();
  }

  /**
   * Login
   */
  loadComponent(): void {
    this.sharedService.navigateToSigninFromSignup(true);
  }

  /**
   * Registration
   */
  goToRegister(): void {
    this.sharedService.navigateToSignupFromSigning(true);
  }

  /**
   * Open Digicel certificate in new tab
   */
  openNewTab(): void {
    if (!this.isBrowser()) {
      return;
    }

    window.open(
      this.appEnv.digicelCertificateUrl,
      '_blank',
      'noopener,noreferrer',
    );
  }

  /**
   * Cookie decision
   */
  decideCookie(choice: number): void {
    /*
     * Keep this parameter because your existing HTML/API
     * may use it in the future.
     */
    console.log('Cookie choice:', choice);

    const element = this.cookieDiv()?.nativeElement;

    if (element) {
      this.renderer.setStyle(
        element,
        'display',
        'none',
      );
    }

    this.isCookieExists.set(false);
  }

  /**
   * Contact us
   */
  goToContactUs(): void {
    const urlTree = this.router.createUrlTree(
      ['/help'],
      {
        queryParams: {
          page: 'contactus',
        },
      },
    );

    if (!this.isBrowser()) {
      return;
    }

    const url = this.router.serializeUrl(urlTree);

    window.open(
      url,
      '_blank',
      'noopener,noreferrer',
    );
  }

  /**
   * Open BBB URL
   */
  openLinkInNewTab(link?: string): void {
    if (!this.isBrowser()) {
      return;
    }

    window.open(
      this.appEnv.bbbUrl,
      '_blank',
      'noopener,noreferrer',
    );
  }

  /**
   * Open supplied URL
   */
  openLinkInNewTabWindow(link: string): void {
    if (!this.isBrowser() || !link) {
      return;
    }

    window.open(
      link,
      '_blank',
      'noopener,noreferrer',
    );
  }

  /**
   * SSR safe browser check
   */
  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
